import { baseApi } from "@/shared/api";
import { fileResponseHandler, createOrgSignedPatch, type FileResponse } from "@/shared/utils";
import type { SigningActionResponse } from "@/shared/types";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import { amendmentsApi } from "./amendments";

export const ordersApi = baseApi.injectEndpoints({
  endpoints(builder) {
    return {
      previewAmendmentOrder: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/amendments/${id}/preview-order/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
      submitAgreement: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
        query({ id, cms_signature, file_base64 }) {
          return {
            url: `/api/hr/amendments/${id}/submit-agreement/`,
            method: "POST",
            body: { cms_signature, file_base64 },
            credentials: "include" as const,
          };
        },
        invalidatesTags() {
          return [
            { type: "Amendments" as const, id: "LIST" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "sign_document" &&
                  "document_id" in item.details &&
                  (item.details as SignDocumentDetails).source_type === "amendment_agreement",
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(
              amendmentsApi.util.updateQueryData("getAmendmentDetail", id, (draft) => {
                draft.status = "agr_pending";
                if (draft.agreement_signing_document) {
                  Object.assign(draft.agreement_signing_document, createOrgSignedPatch());
                }
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      signAmendmentOrder: builder.mutation<SigningActionResponse, { orderId: number; amendmentId: number; cms_signature: string; file_base64: string }>({
        query({ orderId, cms_signature, file_base64 }) {
          return {
            url: `/api/hr/amendment-orders/${orderId}/sign/`,
            method: "POST",
            body: { cms_signature, file_base64 },
            credentials: "include",
          };
        },
        invalidatesTags() {
          return [
            { type: "Amendments" as const, id: "LIST" },
            { type: "Contracts" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
        async onQueryStarted({ amendmentId }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  item.action_required === "sign_document" &&
                  "document_id" in item.details &&
                  (item.details as SignDocumentDetails).source_type === "amendment_order",
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(
              amendmentsApi.util.updateQueryData("getAmendmentDetail", amendmentId, (draft) => {
                draft.status = "order_uploaded";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      previewAgreement: builder.mutation<FileResponse, number>({
        query(id) {
          return {
            url: `/api/hr/amendments/${id}/preview-agreement/`,
            method: "GET",
            credentials: "include",
            responseHandler: fileResponseHandler,
          };
        },
      }),
    };
  },
});

export const {
  usePreviewAmendmentOrderMutation,
  useSignAmendmentOrderMutation,
  useSubmitAgreementMutation,
  usePreviewAgreementMutation,
} = ordersApi;
